import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import QuestionPageLayout from "../components/question/QuestionPageLayout.tsx";

// Utility to shuffle array
function shuffle<T>(array: T[]): T[] {
  return array
    .map((v) => [Math.random(), v] as [number, T])
    .sort((a, b) => a[0] - b[0])
    .map((a) => a[1]);
}

type WordType = "indonesian" | "batak";
type Word = { type: WordType; value: string };
type Pair = { indonesian: string; batak: string };

const QuestionMatchingWordsPage: React.FC<{
  hearts: number;
  onNext: () => void;
  onBack: () => void;
  onRestart: () => void;
  progress: number;
  isLast: boolean;
  onAnswer?: (xp: number, correct: number, total: number) => void;
  onValidate?: () => void;
  onIncorrectAnswer?: () => void;
  darkMode?: boolean;
  questionConfig?: any;
  vocabularyBank?: any[];
  lessonData?: any;
}> = ({
  hearts,
  onNext,
  onBack,
  onRestart,
  progress,
  isLast,
  onAnswer,
  onValidate,
  onIncorrectAnswer,
  darkMode,
  questionConfig,
  vocabularyBank,
  lessonData,
}) => {
    // Use lesson-specific matching pairs or create from vocabulary bank
    const pairs: Pair[] = questionConfig?.pairs ||
      (vocabularyBank || []).slice(0, 5).map((word: any) => ({
        indonesian: word.indonesia,
        batak: word.latin.join(" ")
      }));

    // Create a stable identifier for pairs to avoid infinite re-renders
    const questionId = questionConfig?.id || 'default';
    const configPairsLength = questionConfig?.pairs?.length || 0;
    const vocabLength = vocabularyBank?.length || 0;
    const pairsKey = `${questionId}-${configPairsLength}-${vocabLength}`;

    // Shuffle columns independently - recalculate when pairs change
    const [indoWords, setIndoWords] = useState<string[]>([]);
    const [batakWords, setBatakWords] = useState<string[]>([]);
    const [selected, setSelected] = useState<Word | null>(null);
    const [disabledWords, setDisabledWords] = useState<string[]>([]);
    const [feedback, setFeedback] = useState<{
      [key: string]: "correct" | "incorrect" | null;
    }>({});

    // Track correct pairs
    const [attempts, setAttempts] = useState(0);
    const [correctAttempts, setCorrectAttempts] = useState(0);

    // Reset component state when question changes
    useEffect(() => {
      console.log('QuestionMatchingWordsPage: Resetting state for new question');
      setSelected(null);
      setDisabledWords([]);
      setFeedback({});
      setAttempts(0);
      setCorrectAttempts(0);

      // Regenerate shuffled word arrays
      if (pairs.length > 0) {
        setIndoWords(shuffle(pairs.map((p) => p.indonesian)));
        setBatakWords(shuffle(pairs.map((p) => p.batak)));
      }
    }, [pairsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelect = (word: Word) => {
      if (disabledWords.includes(word.value) || feedback[word.value]) return;

      if (!selected) {
        setSelected(word);
      } else if (selected.type !== word.type) {
        let indo, batak;
        if (word.type === "indonesian") {
          indo = word.value;
          batak = selected.value;
        } else {
          indo = selected.value;
          batak = word.value;
        }
        const isCorrect = pairs.some(
          (p) => p.indonesian === indo && p.batak === batak
        );

        setAttempts((prev) => prev + 1);
        if (isCorrect) setCorrectAttempts((prev) => prev + 1);

        setFeedback((prev) => ({
          ...prev,
          [indo]: isCorrect ? "correct" : "incorrect",
          [batak]: isCorrect ? "correct" : "incorrect",
        }));

        // Call onAnswer for every attempt (for accuracy)
        if (onAnswer) onAnswer(0, isCorrect ? 1 : 0, 1);

        // Reduce heart if answer is incorrect
        if (!isCorrect && onIncorrectAnswer) {
          onIncorrectAnswer();
        }

        if (isCorrect) {
          // Correct pair matched
        }

        setTimeout(() => {
          if (isCorrect) {
            setDisabledWords((prev) => [...prev, indo, batak]);
          }
          setFeedback((prev) => ({
            ...prev,
            [indo]: null,
            [batak]: null,
          }));
        }, 700);

        setSelected(null);
      } else {
        setSelected(word);
      }
    };

    // Track if all pairs are matched
    const totalPairs = pairs.length;
    const matchedCount = disabledWords.length / 2; // each pair disables 2 words

    // Call onAnswer when all pairs are matched (only once)
    useEffect(() => {
      if (matchedCount === totalPairs) {
        if (onAnswer) {
          const xp =
            attempts > 0
              ? Math.min(10, Math.round((correctAttempts / attempts) * 10))
              : 0;
          onAnswer(xp, 0, 0); // Only update XP at the end
        }
        if (onValidate) onValidate();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      matchedCount,
      attempts,
      correctAttempts,
      totalPairs,
      onAnswer,
      onValidate,
    ]);

  const isMobile = useMediaQuery({ maxWidth: 767 });
  return (
    <QuestionPageLayout
      hearts={hearts}
      onBack={onBack}
      progress={progress}
      darkMode={darkMode}
    >
      <h1
        className={`${
          isMobile ? "text-lg mb-4" : "text-2xl mb-8"
        } font-bold text-center ${
          darkMode ? "text-gray-100" : "text-gray-800"
        }`}
        style={{ fontFamily: "'Feather Bold'" }}
      >
        Cocokkan pasangan kata!
      </h1>
      <div
        className={`flex flex-col items-center ${
          isMobile ? "gap-2 mb-5" : "gap-4 mb-8"
        }`}
      >
        {indoWords.map((indo, idx) => {
          const batak = batakWords[idx];
          // ...existing code...
          const getButtonClass = (state, matched, selected, isBatak) => {
            if (state === "correct")
              return darkMode
                ? "bg-lime-800 border-lime-400 text-lime-200"
                : "bg-lime-100 border-lime-400 text-lime-800";
            if (state === "incorrect")
              return darkMode
                ? "bg-red-700 border-red-400 text-red-200"
                : "bg-red-100 border-red-400 text-red-800";
            if (matched)
              return darkMode
                ? "bg-gray-700 border-gray-700 text-gray-400"
                : "bg-gray-100 border-gray-300 text-gray-400";
            if (selected)
              return darkMode
                ? "bg-blue-900 border-blue-400 text-blue-200"
                : "bg-blue-100 border-blue-400";
            return darkMode
              ? "bg-gray-800 border-gray-700 text-gray-100 hover:bg-gray-800"
              : "bg-white border-gray-300 hover:bg-gray-50";
          };
          return (
            <div
              key={indo + batak}
              className={`flex flex-row w-full mx-auto ${
                isMobile ? "gap-3 max-w-xs" : "gap-8 max-w-xl"
              }`}
            >
              <button
                disabled={
                  disabledWords.includes(indo) || feedback[indo] === "correct"
                }
                onClick={() =>
                  handleSelect({ type: "indonesian", value: indo })
                }
                className={`flex-1 rounded-lg border transition-colors text-center ${
                  isMobile ? "px-2 py-2 text-base" : "px-6 py-4 text-lg"
                } ${getButtonClass(
                  feedback[indo],
                  disabledWords.includes(indo),
                  selected && selected.value === indo,
                  false
                )}`}
                style={{
                  fontFamily: "'DIN Next Rounded', Arial, sans-serif",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                {indo}
              </button>
              <button
                disabled={
                  disabledWords.includes(batak) || feedback[batak] === "correct"
                }
                onClick={() => handleSelect({ type: "batak", value: batak })}
                className={`flex-1 rounded-lg border transition-colors text-center ${
                  isMobile ? "px-2 py-2 text-lg" : "px-6 py-4 text-2xl"
                } ${getButtonClass(
                  feedback[batak],
                  disabledWords.includes(batak),
                  selected && selected.value === batak,
                  true
                )}`}
                style={{
                  fontFamily: "'Noto Sans Batak'",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                {batak}
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-center mt-6">
        {matchedCount === totalPairs ? (
          <button
            onClick={onNext}
            className={`rounded-lg font-bold transition-colors ${
              isMobile ? "px-5 py-2 text-sm" : "px-8 py-3 text-base"
            } ${
              darkMode
                ? "bg-lime-600 hover:bg-lime-700 text-white"
                : "bg-lime-500 hover:bg-lime-600 text-white"
            }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {isLast ? "Selesai" : "Lanjutkan"}
          </button>
        ) : (
          <button
            disabled
            className={`rounded-lg font-bold transition-colors ${
              isMobile ? "px-5 py-2 text-sm" : "px-8 py-3 text-base"
            } ${
              darkMode ? "bg-gray-700 text-gray-300" : "bg-gray-400 text-white"
            } cursor-not-allowed`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            Periksa
          </button>
        )}
      </div>
    </QuestionPageLayout>
  );
};

export default QuestionMatchingWordsPage;
